import type {
  MessagingChannel,
  MessagingLinkState,
  MessagingProvider,
  MessagingPublicIdentityMetadata,
  MessagingStatusResponse,
  WhatsAppLinkState,
} from "@/api/client";

export const WHATSAPP_CONSENT_VERSION = "2026-07-10.whatsapp-v1";
export const SIGNAL_E2E_CONSENT_VERSION = "2026-07-10.signal-e2e-v1";
export const SIGNAL_CONSENT_VERSION = "2026-07-19.signal-v1";
export const TELEGRAM_CONSENT_VERSION = "2026-07-24.telegram-v1";
export const WHATSAPP_REGION_AVAILABILITY =
  "Podwise on WhatsApp is currently available only to WhatsApp users registered in EEA countries or Brazil.";

export interface SafeWhatsAppIdentityMetadata {
  countryCode: string | null;
  identityLabel: string | null;
}

export type SafeMessagingIdentityMetadata = SafeWhatsAppIdentityMetadata;

export interface MessagingConnection {
  channel: MessagingChannel;
  provider: MessagingProvider;
  state: MessagingLinkState;
  identity: MessagingPublicIdentityMetadata & {
    linked_at?: string | null;
    consent_at?: string | null;
  };
}

function normalizeCountryCode(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase() ?? "";
  return /^[A-Z]{2}$/.test(normalized) && normalized !== "ZZ" ? normalized : null;
}

export function getSafeMessagingIdentityMetadata(
  metadata: MessagingPublicIdentityMetadata | null | undefined,
): SafeMessagingIdentityMetadata {
  const identityLabel = metadata?.provider === "signal"
    ? "Signal account"
    : metadata?.provider === "telegram_bot_api"
      ? "Telegram account"
    : metadata?.provider === "signal_e2e"
      ? "Legacy Signal staging identity"
    : metadata?.identity_type === "username_only"
      ? "Username-only WhatsApp account"
    : metadata?.identity_type === "phone_and_username"
      ? "Standard WhatsApp account"
      : null;

  return {
    countryCode: normalizeCountryCode(metadata?.country_code),
    identityLabel,
  };
}

export const getSafeWhatsAppIdentityMetadata = getSafeMessagingIdentityMetadata;

export function getMessagingConnectionErrorMessage(
  error: unknown,
  provider: MessagingProvider,
): string {
  const message = error instanceof Error ? error.message : "";
  if (provider === "signal" || provider === "signal_e2e") {
    if (message.includes("403") || message.includes("422") || message.includes("451")) {
      return "This Signal identity is not eligible for this connection.";
    }
    if (message.includes("409")) {
      return "Podwise couldn't connect this Signal identity. For privacy, we can't say whether it is connected elsewhere. Review your connection in Settings or request a new setup link from Signal.";
    }
    if (message.includes("410") || message.includes("400")) {
      return "This setup link is invalid, expired, or already used. Return to Signal and request a new link.";
    }
    return "We couldn't connect this Signal identity right now. Please try again.";
  }
  if (provider === "telegram_bot_api") {
    if (message.includes("403") || message.includes("422") || message.includes("451")) {
      return "This Telegram identity is not eligible for this connection.";
    }
    if (message.includes("409")) {
      return "Podwise couldn't connect this Telegram identity. For privacy, we can't say whether it is connected elsewhere. Review your connection in Settings or request a new setup link from Telegram.";
    }
    if (message.includes("410") || message.includes("400")) {
      return "This setup link is invalid, expired, or already used. Return to Telegram and request a new link.";
    }
    return "We couldn't connect this Telegram identity right now. Please try again.";
  }
  if (message.includes("403") || message.includes("422") || message.includes("451")) {
    return WHATSAPP_REGION_AVAILABILITY;
  }
  if (message.includes("409")) {
    return "Podwise couldn't connect this WhatsApp identity. For privacy, we can't say whether it is connected elsewhere. Review your connection in Settings or request a new setup link from WhatsApp.";
  }
  if (message.includes("410") || message.includes("400")) {
    return "This setup link is invalid, expired, or already used. Return to WhatsApp and request a new link.";
  }
  return "We couldn't connect this WhatsApp account right now. Please try again.";
}

const AUTH_NEXT_PATHS = new Set([
  "/billing",
  "/episodes",
  "/paywall",
  "/profile",
  "/text/connect",
]);

export function getMessagingSetupToken(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.pathname !== "/text/connect") return null;

    // Keep the setup capability in the fragment so it is never sent to the
    // CDN, origin server, referrer, or access logs.
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
    const token = fragment.get("t")?.trim() ?? "";
    if (!token || token.length > 2048 || /\s/.test(token)) return null;
    return token;
  } catch {
    return null;
  }
}

export function stripMessagingSetupToken(href: string): string {
  try {
    const url = new URL(href);
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
    fragment.delete("t");
    const search = url.searchParams.toString();
    const hash = fragment.toString();
    return `${url.pathname}${search ? `?${search}` : ""}${hash ? `#${hash}` : ""}`;
  } catch {
    return "/text/connect";
  }
}

export function resolveAuthNextPath(
  nextPath: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): string {
  const fallback = subscriptionStatus === "active" ? "/episodes" : "/paywall";
  if (!nextPath || nextPath.includes("\\")) return fallback;

  try {
    const url = new URL(nextPath, "https://podwise.local");
    if (url.origin !== "https://podwise.local" || !AUTH_NEXT_PATHS.has(url.pathname)) {
      return fallback;
    }
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
    if (
      url.searchParams.has("t")
      || url.searchParams.has("token")
      || url.searchParams.has("setup_token")
      || fragment.has("t")
      || fragment.has("token")
      || fragment.has("setup_token")
    ) {
      return fallback;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

export function getWhatsAppState(status: MessagingStatusResponse | null): WhatsAppLinkState {
  return status?.whatsapp?.state ?? "unlinked";
}

export function getMessagingConnection(
  status: MessagingStatusResponse | null,
): MessagingConnection | null {
  const signal = status?.signal ?? null;
  const telegram = status?.telegram ?? null;
  const whatsapp = status?.whatsapp ?? null;
  if (
    signal &&
    (
      signal.state !== "unlinked" ||
      (!telegram || telegram.state === "unlinked") &&
      (!whatsapp || whatsapp.state === "unlinked")
    )
  ) {
    return {
      channel: "signal",
      provider: signal.provider ?? "signal",
      state: signal.state,
      identity: signal,
    };
  }
  if (
    telegram &&
    (
      telegram.state !== "unlinked" ||
      !whatsapp ||
      whatsapp.state === "unlinked"
    )
  ) {
    return {
      channel: "telegram",
      provider: telegram.provider ?? "telegram_bot_api",
      state: telegram.state,
      identity: telegram,
    };
  }
  if (whatsapp) {
    return {
      channel: "whatsapp",
      provider: whatsapp.provider ?? "twilio",
      state: whatsapp.state,
      identity: whatsapp,
    };
  }
  return null;
}

export function getMessagingState(
  status: MessagingStatusResponse | null,
  channel: MessagingChannel = "signal",
): MessagingLinkState {
  return (
    channel === "signal"
      ? status?.signal?.state
      : channel === "telegram"
        ? status?.telegram?.state
        : status?.whatsapp?.state
  ) ?? "unlinked";
}

export function getAvailableMessagingChannels(
  status: MessagingStatusResponse | null,
): MessagingChannel[] {
  const channels: MessagingChannel[] = ["signal", "telegram"];
  if (
    status?.whatsapp_entry_url ||
    (status?.whatsapp?.state && status.whatsapp.state !== "unlinked")
  ) {
    channels.push("whatsapp");
  }
  return channels;
}

export function getSafeTelegramEntryURL(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:" ||
      url.hostname !== "t.me" ||
      url.username !== "" ||
      url.password !== ""
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function getSafeWhatsAppEntryURL(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    const url = new URL(value);
    if (url.protocol === "whatsapp:") return url.toString();
    if (url.protocol !== "https:") return null;
    if (url.hostname !== "wa.me" && url.hostname !== "api.whatsapp.com") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function formatRetentionDays(value: number | undefined, fallback: number): string {
  const days = Number.isFinite(value) && (value ?? 0) > 0 ? Math.round(value as number) : fallback;
  return `${days} day${days === 1 ? "" : "s"}`;
}
